import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearTokens,
} from './api';

// Empty string = same-origin (auth proxied through CloudFront)
const AUTH_URL = import.meta.env.VITE_AUTH_URL ?? '';

export { AUTH_URL };

// Shared refresh state — coordinated between apiClient and authApiClient
// Both clients use this same module-level state to prevent concurrent refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Shared 401 refresh handler used by both API clients.
 * Refresh always goes to auth service.
 */
export async function handleTokenRefresh(
  error: AxiosError,
  retryFn: (originalRequest: InternalAxiosRequestConfig) => Promise<AxiosResponse>
): Promise<AxiosResponse> {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
  };

  const isLoginRequest = originalRequest.url?.includes('/api/auth/login');
  const isRefreshRequest = originalRequest.url?.includes('/api/auth/refresh');
  const isAuthEndpoint = isLoginRequest || isRefreshRequest;

  if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          return retryFn(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      // Refresh always goes to auth service
      const response = await axios.post(`${AUTH_URL}/api/auth/refresh`, {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token } = response.data;
      setAccessToken(access_token);
      setRefreshToken(refresh_token);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
      }

      processQueue(null, access_token);
      isRefreshing = false;

      return retryFn(originalRequest);
    } catch (refreshError) {
      const axiosError = refreshError as AxiosError;
      processQueue(axiosError, null);
      isRefreshing = false;

      const status = axiosError.response?.status;
      if (status && status >= 400 && status < 500) {
        clearTokens();
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  }

  return Promise.reject(error);
}

// Create auth service axios instance
const authApiClient = axios.create({
  baseURL: AUTH_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null,
  },
});

// Request interceptor — inject same access token
authApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 with shared refresh logic
authApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    return handleTokenRefresh(error, (req) => authApiClient(req));
  }
);

// Auth API helper functions
export async function authApiGet<T>(url: string, params?: unknown): Promise<T> {
  const response = await authApiClient.get<T>(url, { params });
  return response.data;
}

export async function authApiPost<T>(url: string, data?: unknown): Promise<T> {
  const response = await authApiClient.post<T>(url, data);
  return response.data;
}

export async function authApiPut<T>(url: string, data?: unknown): Promise<T> {
  const response = await authApiClient.put<T>(url, data);
  return response.data;
}

export default authApiClient;
